import type { ListConfig } from "./types.js";

export const DEFAULT_ENDPOINT = "https://api.slater.app";
const DEFAULT_PAGE_SIZE = 12;

let debugEnabled = false;

export function initDebug(): void {
  const params = new URLSearchParams(window.location.search);
  debugEnabled =
    params.has("slater-debug") ||
    Boolean(document.querySelector("script[data-slater-debug]"));
}

export function debug(...args: unknown[]): void {
  if (debugEnabled) console.info("[slater-search]", ...args);
}

export function warn(...args: unknown[]): void {
  console.warn("[slater-search]", ...args);
}

export function intAttr(el: Element, name: string, fallback: number): number {
  const raw = el.getAttribute(name);
  if (!raw) return fallback;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/** Splits a comma-separated attribute into trimmed, non-empty parts. */
export function csv(raw: string): string[] {
  return raw.split(",").map((v) => v.trim()).filter(Boolean);
}

/**
 * Resolves one list's config. Attributes on the wrapper win over
 * `window.SlaterSearchConfig`, which wins over derived defaults — so a Webflow
 * developer can drive the whole thing from markup, or from a config block, or
 * mix the two.
 *
 * Both `siteId` and `locale` are derivable from the page: Webflow stamps
 * `data-wf-site` on `<html>`, and `<html lang>` is the current locale. That's
 * what makes the zero-config path work.
 */
export function resolveListConfig(wrapper: Element, key: string): ListConfig | null {
  const global = window.SlaterSearchConfig ?? {};
  const perList = global.lists?.[key] ?? {};

  const siteId =
    wrapper.getAttribute("data-slater-site") ??
    perList.siteId ??
    global.siteId ??
    document.documentElement.getAttribute("data-wf-site") ??
    "";

  if (!siteId) {
    warn(
      `list "${key}": no site id. Set data-slater-site, SlaterSearchConfig.siteId, ` +
        `or publish from Webflow (which stamps data-wf-site on <html>).`,
    );
    return null;
  }

  const collection =
    wrapper.getAttribute("data-slater-collection") ?? perList.collection ?? key;

  // A dashboard-defined collection group. When set, the datasource queries
  // /:siteId/group/:slug and `collection` is never used — an explicitly
  // authored collection alongside it is almost certainly a mistake, hence the
  // debug note (behaviour stays well-defined: group wins).
  const group =
    (wrapper.getAttribute("data-slater-group") ?? perList.group ?? "").trim() || undefined;
  if (group && (wrapper.getAttribute("data-slater-collection") ?? perList.collection)) {
    debug(`list "${key}": data-slater-group is set — data-slater-collection is ignored.`);
  }

  const pageAttr = wrapper.getAttribute("data-slater-page-size");
  const initialLimit = pageAttr
    ? intAttr(wrapper, "data-slater-page-size", DEFAULT_PAGE_SIZE)
    : perList.initialLimit ?? DEFAULT_PAGE_SIZE;

  const loadMoreAmount =
    intAttr(wrapper, "data-slater-load-more-amount", 0) ||
    perList.loadMoreAmount ||
    initialLimit;

  // An empty <html lang> or an explicit "" means "primary locale" — send nothing.
  const locale =
    wrapper.getAttribute("data-slater-locale") ??
    perList.locale ??
    global.locale ??
    document.documentElement.getAttribute("lang") ??
    undefined;

  const facetsAttr = wrapper.getAttribute("data-slater-facets");
  const expandAttr = wrapper.getAttribute("data-slater-expand");
  const searchFieldsAttr = wrapper.getAttribute("data-slater-search-fields");

  // Base filter (config only). Each field's value may be a string or an array.
  const baseFilter: Record<string, string[]> = {};
  for (const [field, value] of Object.entries(perList.filter ?? {})) {
    const values = (Array.isArray(value) ? value : [value])
      .map((v) => String(v).trim())
      .filter(Boolean);
    if (values.length > 0) baseFilter[field] = values;
  }

  return {
    key,
    mode: "list",
    endpoint: (perList.endpoint ?? global.endpoint ?? DEFAULT_ENDPOINT).replace(/\/+$/, ""),
    siteId,
    collection,
    group,
    locale: locale || undefined,
    initialLimit,
    loadMoreAmount,
    minLength: 1,
    sort: wrapper.getAttribute("data-slater-default-sort") ?? perList.sort ?? undefined,
    detailPath: wrapper.getAttribute("data-slater-detail-path") ?? perList.detailPath ?? undefined,
    facets: facetsAttr ? csv(facetsAttr) : perList.facets ?? [],
    // A bare `data-slater-expand` (present but empty) means "all reference fields",
    // which is the whole point for a no-code user.
    expand:
      expandAttr === null
        ? perList.expand ?? []
        : csv(expandAttr).length > 0
          ? csv(expandAttr)
          : ["*"],
    searchFields: searchFieldsAttr
      ? csv(searchFieldsAttr)
      : perList.searchFields ?? [],
    baseFilter,
  };
}

